import { useEffect, useRef } from 'react';
import { EmbeddedGatewayFrame } from './EmbeddedGatewayFrame';
import { performRedirect } from './redirect';
import { attachApplePaySheet, discardApplePaySheet, lastSheetFailure } from './wallets/applePaySheet';
import { openGooglePaySheet } from './wallets/googlePay';
import { isWalletCancelled } from './wallets/walletErrors';
import { describeSheetFailure } from './wallets/sheetFailure';
import { LoadingScreen } from '@/features/check/StatusScreen';
import { useTranslation } from '@/i18n';
import type { Location, PaymentAction } from '@/api/types';

interface PaymentActionHostProps {
    action: PaymentAction;

    /** Whose restaurant this is, for the brand above the gateway's own card form. */
    location: Location;

    /**
     * Forwards the gateway payload and reports whether the server settled it.
     *
     * The return value exists for Apple Pay, which cannot dismiss its own sheet until it knows: the tick or
     * cross a guest sees is then the real outcome rather than an optimistic guess. Everything else ignores it.
     */
    onResult: (result: Record<string, unknown>) => Promise<boolean>;

    /** Exchanges Apple's validation URL for a merchant session, via our server. */
    onValidateMerchant?: (validationUrl: string) => Promise<unknown>;

    /**
     * Called when this build cannot obey the action, so the caller can fail honestly rather than hang.
     *
     * `detail` is the browser's own account of why a wallet sheet would not open — shown only under
     * diagnostics, never to a guest. It is the only record of that failure anywhere: it happens before the
     * sheet ever contacts Apple, so no server sees it.
     */
    onUnsupported: (detail?: string) => void;


    /** Called when the guest dismissed a wallet sheet. Not a failure — they changed their mind. */
    onCancelled?: () => void;
}

/**
 * Obeys one `PaymentAction`, whatever it is.
 *
 * The single place the client acts on the server's instruction, so `type` is switched on exactly once in the
 * app. A guest arriving back from 3-D Secure runs through here identically to one starting a card payment —
 * a challenge is just another action, which is why it needs no machinery of its own.
 */
export function PaymentActionHost({
    action,
    location,
    onResult,
    onValidateMerchant,
    onUnsupported,
    onCancelled,
}: PaymentActionHostProps) {
    const { t } = useTranslation();

    /*
     * Callbacks through a ref so the effect below depends on the action alone. Without this, a caller passing
     * inline arrows would re-run it on every render — and re-running it means opening a second wallet sheet
     * or firing a second navigation.
     */
    const handlers = useRef({ onResult, onValidateMerchant, onUnsupported, onCancelled });
    useEffect(() => {
        handlers.current = { onResult, onValidateMerchant, onUnsupported, onCancelled };
    }, [onResult, onValidateMerchant, onUnsupported, onCancelled]);

    useEffect(() => {
        let abandoned = false;

        /** A sheet outlives this component if the guest navigates away; ignore whatever it says then. */
        const onSheetFailed = (error: unknown) => {
            if (abandoned) return;

            if (isWalletCancelled(error)) handlers.current.onCancelled?.();
            else handlers.current.onUnsupported(describeSheetFailure(error));
        };

        if (action.type === 'redirect') {
            if (!performRedirect(action)) handlers.current.onUnsupported('The gateway redirect could not be performed.');
            return;
        }

        if (action.type === 'googlePay') {
            openGooglePaySheet(action)
                .then((result) => {
                    if (!abandoned) void handlers.current.onResult(result);
                })
                .catch(onSheetFailed);
        }

        if (action.type === 'applePay') {
            /*
             * ATTACHED, NOT OPENED. The sheet was constructed by the tap that chose Apple Pay, several
             * hundred milliseconds and one navigation ago, because Safari refuses to build one anywhere else.
             * What is left here is wiring it to the things only this component knows how to do.
             */
            const validateMerchant = handlers.current.onValidateMerchant;

            /*
             * Apple Pay cannot run without somewhere to send its validation URL. Saying so immediately beats
             * leaving a sheet open that stalls halfway through with the guest's thumb on the sensor.
             */
            if (!validateMerchant) {
                discardApplePaySheet();
                handlers.current.onUnsupported('Apple Pay needs a merchant validation route and this build has none.');
                return;
            }

            const running = attachApplePaySheet({
                validateMerchant,
                authorize: (payment) => handlers.current.onResult(payment),
            });

            /*
             * No sheet to take over. Either the tap never managed to build one - `lastSheetFailure` is what
             * it said at the time - or this step was reached without a tap at all, which a reload or a
             * recovery link will do. Both are honest failures rather than a screen that waits forever.
             */
            if (!running) {
                handlers.current.onUnsupported(
                    lastSheetFailure() ?? 'The Apple Pay sheet was not opened by the tap that asked for it.',
                );
                return;
            }

            running.catch(onSheetFailed);
        }

        return () => {
            abandoned = true;
        };
    }, [action]);

    if (action.type === 'embedded') {
        return (
            <EmbeddedGatewayFrame
                action={action}
                location={location}
                title={t('payment.cardFrameTitle')}
                onResult={onResult}
            />
        );
    }

    /*
     * A wallet sheet floats above the page and a redirect is on its way out, so there is nothing of our own
     * to draw — but a blank page behind a sheet reads as a crash the moment a guest dismisses it.
     */
    if (action.type === 'googlePay' || action.type === 'applePay') return <LoadingScreen />;

    return null;
}
